import React, { useState, useEffect, useRef } from "react";
import { Search, X, Check, Loader2 } from "lucide-react";
import api from "../../../services/api";

/**
 * Modern, debounced, searchable dropdown for MBBS Competencies.
 * Styled to match all standard form inputs (Activity Title, Date, Type, etc.).
 * Exclusively available for MBBS students.
 */
const CompetencySearchSelect = ({ competencies = [], onChange }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Debounced search API call
  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const timer = setTimeout(() => {
      api
        .get(`/auth/student/mbbs-competencies/?q=${encodeURIComponent(query.trim())}&limit=40`)
        .then((res) => {
          setResults(res.data?.results || []);
        })
        .catch((err) => {
          console.error("Failed to search MBBS competencies:", err);
          setResults([]);
        })
        .finally(() => {
          setLoading(false);
        });
    }, 280);

    return () => clearTimeout(timer);
  }, [query]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (comp) => {
    // Check if already selected
    const exists = competencies.some((c) => c.id === comp.id || c.code === comp.code);
    if (!exists) {
      const updated = [...competencies, comp];
      onChange(updated);
    }
    setQuery("");
    setIsOpen(false);
  };

  const handleRemove = (indexToRemove) => {
    const updated = competencies.filter((_, idx) => idx !== indexToRemove);
    onChange(updated);
  };

  return (
    <div className="space-y-2 font-sans" ref={dropdownRef}>
      <label className="block text-sm font-medium text-gray-700 mb-1.5">
        Competencies
      </label>

      {/* Search Input Box - Matching standard input field styling */}
      <div className="relative">
        <div className="relative flex items-center">
          <Search className="w-4 h-4 text-gray-400 absolute left-3.5 pointer-events-none" />
          <input
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setIsOpen(true);
            }}
            onFocus={() => setIsOpen(true)}
            placeholder="Search competency number (e.g., CM1, IM2.1) or description..."
            className="w-full pl-10 pr-10 py-2.5 text-sm bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200"
          />
          {loading && (
            <Loader2 className="w-4 h-4 text-blue-600 animate-spin absolute right-3.5 pointer-events-none" />
          )}
          {!loading && query && (
            <button
              type="button"
              onClick={() => {
                setQuery("");
                setResults([]);
              }}
              className="absolute right-3.5 text-gray-400 hover:text-gray-600 p-0.5"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Dropdown Results Panel */}
        {isOpen && (query.trim().length > 0 || results.length > 0) && (
          <div className="absolute z-50 mt-1.5 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-y-auto divide-y divide-gray-100">
            {loading ? (
              <div className="p-4 text-center text-xs font-medium text-gray-500 flex items-center justify-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                Searching competencies...
              </div>
            ) : results.length === 0 ? (
              <div className="p-4 text-center text-xs text-gray-500 font-medium">
                No matching competencies found for "{query}". Try searching by code (CM1, PA26) or keyword.
              </div>
            ) : (
              results.map((comp) => {
                const isSelected = competencies.some((c) => c.id === comp.id || c.code === comp.code);
                return (
                  <button
                    key={comp.id || comp.code}
                    type="button"
                    onClick={() => handleSelect(comp)}
                    disabled={isSelected}
                    className={`w-full text-left p-3 flex items-start gap-3 transition-colors ${
                      isSelected
                        ? "bg-gray-50 opacity-60 cursor-not-allowed"
                        : "hover:bg-gray-50 cursor-pointer"
                    }`}
                  >
                    {/* Badge: White background with black text */}
                    <span className="inline-block px-2.5 py-1 rounded bg-white text-gray-900 border border-gray-300 font-mono text-xs font-bold shrink-0 mt-0.5 shadow-xs">
                      {comp.code}
                    </span>
                    <span className="text-xs text-gray-700 font-medium flex-1 leading-relaxed">
                      {comp.description}
                    </span>
                    {isSelected && (
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full shrink-0 border border-emerald-200">
                        <Check className="w-3 h-3" /> Selected
                      </span>
                    )}
                  </button>
                );
              })
            )}
          </div>
        )}
      </div>

      {/* Selected Competencies Tags Display */}
      {competencies.length > 0 && (
        <div className="space-y-2 pt-2">
          <div className="text-xs font-semibold text-gray-600 uppercase tracking-wider">
            Selected Competencies ({competencies.length})
          </div>
          <div className="flex flex-col gap-2">
            {competencies.map((comp, index) => (
              <div
                key={comp.id || comp.code || index}
                className="bg-white border border-gray-200 rounded-lg p-3 flex items-start justify-between gap-3 shadow-xs hover:border-gray-300 transition-colors"
              >
                <div className="flex items-start gap-2.5 min-w-0">
                  {/* Badge: White background with black text */}
                  <span className="inline-block px-2.5 py-1 rounded bg-white text-gray-900 border border-gray-300 font-mono text-xs font-bold shrink-0 mt-0.5 shadow-xs">
                    {comp.code}
                  </span>
                  <p className="text-xs font-medium text-gray-800 leading-snug break-words">
                    {comp.description}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemove(index)}
                  className="text-gray-400 hover:text-red-600 p-1 rounded-md hover:bg-red-50 transition-colors shrink-0"
                  title="Remove competency"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CompetencySearchSelect;
